import {
  profile,
  bioParagraphs,
  profilePhilosophy,
  family,
  timeline,
  ventures,
  education,
  positions,
  memberships,
} from "../data/profileData";
import ChapterMark from "../components/ChapterMark";
import GoldDivider from "../components/GoldDivider";
import { GraduationCap } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, staggerContainer, staggerItem } from "../components/Reveal";
import Seo from "../components/Seo";

const roman = (n) => {
  if (n < 1) return "—";
  const map = { M:1000, CM:900, D:500, CD:400, C:100, XC:90, L:50, XL:40, X:10, IX:9, V:5, IV:4, I:1 };
  let r = "", num = n;
  for (const k in map) { while (num >= map[k]) { r += k; num -= map[k]; } }
  return r;
};

export default function Profile() {
  return (
    <div data-testid="profile-page" className="bg-white">
      <Seo
        path="/profile"
        title="Profile"
        description="The story of Dr. P. B. Boss — three decades building the Osaka Group across travel, visa, overseas education, technology and real estate, spanning India, the UK, Australia, Canada, USA, UAE and Singapore."
      />
      {/* ===== TITLE PAGE ===== */}
      <section
        data-testid="profile-hero"
        className="pt-44 pb-24 lg:pt-52 lg:pb-32 bg-white relative"
      >
        <span className="hidden lg:block absolute left-7 top-1/2 -translate-y-1/2 v-label">
          P · B · Boss — Profile
        </span>
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 text-center">
          <ChapterMark numeral="I" label="The Profile" />
          <h1 className="mt-14 font-couture text-[clamp(3.5rem,9vw,8rem)] leading-[0.94] tracking-[-0.025em] text-[var(--ink)] reveal">
            Succeeding in business
            <br />
            is all about
            <br />
            <span className="font-couture-italic text-[var(--gold-deep)]">
              making connections.
            </span>
          </h1>
          <p className="mt-10 font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--ink-muted)]">
            — after Richard Branson
          </p>
        </div>
      </section>

      {/* Hero portrait — full width band */}
      <section className="bg-[var(--bg-soft)] py-16 lg:py-24">
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <Reveal y={48} className="img-frame aspect-[16/8] overflow-hidden">
            <img
              src={profile.desk_portrait}
              alt={profile.name}
              className="w-full h-full object-cover img-soft"
            />
          </Reveal>
        </div>
      </section>

      {/* ===== BIO with drop cap ===== */}
      <section data-testid="profile-bio" className="py-32 lg:py-44">
        <div className="max-w-[860px] mx-auto px-7 sm:px-12">
          <Reveal className="text-center mb-16">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)]">
              §  Biography
            </p>
            <GoldDivider className="mt-5" />
          </Reveal>

          <Reveal delay={0.1} className="space-y-9 font-couture text-[1.35rem] lg:text-[1.5rem] leading-[1.65] text-[var(--ink)]" style={{ fontWeight: 400 }}>
            {bioParagraphs.map((p, i) => (
              <p
                key={i}
                data-testid={`bio-para-${i}`}
                className={i === 0 ? "dropcap" : ""}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {p}
              </p>
            ))}
          </Reveal>

          <Reveal className="mt-20 pt-12 border-t border-[var(--gold-line)] text-center">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-7">
              Philosophy
            </p>
            <blockquote className="font-couture-italic text-2xl lg:text-[1.85rem] text-[var(--ink-soft)] leading-[1.5] max-w-[42rem] mx-auto">
              <span className="text-[var(--gold)] font-couture text-5xl leading-none mr-1 align-top">
                &ldquo;
              </span>
              {profilePhilosophy}
              <span className="text-[var(--gold)] font-couture text-5xl leading-none ml-1 align-top">
                &rdquo;
              </span>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* ===== FAMILY ===== */}
      <section
        data-testid="profile-family"
        className="py-32 lg:py-44 bg-[var(--bg-soft)]"
      >
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <div className="text-center mb-20">
            <ChapterMark numeral="II" label="Family" />
            <h2 className="mt-10 font-couture text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[1]">
              The people at the
              <br />
              <span className="font-couture-italic text-[var(--gold-deep)]">
                centre.
              </span>
            </h2>
          </div>

          <motion.div
            className="grid md:grid-cols-3 gap-10 lg:gap-16"
            variants={staggerContainer(0.12)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            {family.map((f, i) => (
              <motion.article
                key={f.name}
                data-testid={`family-${i}`}
                variants={staggerItem}
                className="text-center"
              >
                <div className="img-frame aspect-[3/4] overflow-hidden mb-8">
                  <img
                    src={f.image}
                    alt={f.name}
                    className="w-full h-full object-cover img-soft"
                    loading="lazy"
                  />
                </div>
                <span className="font-marcellus uppercase tracking-[0.42em] text-[0.62rem] text-[var(--gold-deep)]">
                  Portrait {roman(i + 1)}
                </span>
                <h3 className="mt-3 font-couture text-3xl text-[var(--ink)] tracking-[-0.01em]">
                  {f.name}
                </h3>
                <p className="mt-2 font-couture-italic text-lg text-[var(--gold-deep)]">
                  {f.role}
                </p>
                <p className="mt-5 font-body text-base text-[var(--ink-soft)] leading-relaxed max-w-xs mx-auto">
                  {f.note}
                </p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== TIMELINE ===== */}
      <section data-testid="profile-timeline" className="py-32 lg:py-44">
        <div className="max-w-[1100px] mx-auto px-7 sm:px-12">
          <div className="text-center mb-20">
            <ChapterMark numeral="III" label="History & Roadmap" />
            <h2 className="mt-10 font-couture text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[1]">
              Milestones across
              <br />
              <span className="font-couture-italic text-[var(--gold-deep)]">
                three decades.
              </span>
            </h2>
          </div>

          <ol className="relative">
            <div className="absolute left-[8.5rem] top-0 bottom-0 w-px bg-[var(--gold-line)] hidden md:block" />
            {timeline.map((t, i) => (
              <motion.li
                key={t.year}
                data-testid={`timeline-${i}`}
                variants={staggerItem}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="relative flex flex-col md:flex-row gap-3 md:gap-14 py-9 md:items-baseline group"
              >
                <span className="font-couture text-5xl lg:text-6xl text-[var(--gold-deep)] md:w-36 md:text-right shrink-0 tabular-nums leading-none tracking-tight">
                  {t.year}
                </span>
                <span className="hidden md:block absolute left-[8.5rem] -translate-x-1/2 top-12 w-2.5 h-2.5 rotate-45 bg-[var(--gold)] ring-[6px] ring-white transition-transform group-hover:scale-125" />
                <span className="font-couture-italic text-xl lg:text-[1.55rem] text-[var(--ink)] leading-snug md:ml-12">
                  {t.event}
                </span>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* ===== VENTURES — list, no cards ===== */}
      <section
        data-testid="profile-ventures"
        className="py-32 lg:py-44 bg-[var(--bg-soft)] relative"
      >
        <span className="hidden lg:block absolute right-7 top-24 v-label">
          IV — Professional Leadership
        </span>

        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <div className="mb-20 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <ChapterMark numeral="IV" label="Professional Leadership" align="left" />
              <h2 className="mt-10 font-couture text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[0.98]">
                Twelve ventures.
                <br />
                <span className="font-couture-italic text-[var(--gold-deep)]">
                  One vision.
                </span>
              </h2>
            </div>
            <div className="lg:col-span-4 lg:pl-12 lg:border-l border-[var(--gold-line)]">
              <p className="font-couture-italic text-xl lg:text-[1.4rem] text-[var(--ink-soft)] leading-[1.55]">
                A federation of enterprises across travel, education, technology
                and real estate — operating in seven countries with a single
                Kerala-born ethos.
              </p>
            </div>
          </div>

          <ul className="border-t border-[var(--gold-line)]">
            {ventures.map((v, i) => (
              <motion.li
                key={v.name}
                data-testid={`venture-${i}`}
                variants={staggerItem}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="grid grid-cols-12 gap-6 py-9 lg:py-11 border-b border-[var(--gold-line)] hover:bg-white transition-colors"
              >
                <span className="col-span-2 lg:col-span-1 numeral text-2xl lg:text-3xl text-[var(--gold-deep)]">
                  {roman(i + 1)}
                </span>
                <div className="col-span-10 lg:col-span-6">
                  <h3 className="font-couture text-2xl lg:text-[2rem] text-[var(--ink)] leading-tight tracking-[-0.01em]">
                    {v.name}
                  </h3>
                  <p className="mt-2 font-marcellus uppercase tracking-[0.32em] text-[0.66rem] text-[var(--gold-deep)]">
                    {v.role} <span className="text-[var(--ink-muted)] mx-2">·</span>
                    <span className="text-[var(--ink-muted)] normal-case tracking-normal font-couture-italic text-base">
                      {v.where}
                    </span>
                  </p>
                </div>
                <p className="col-span-12 lg:col-span-5 font-couture-italic text-lg lg:text-xl text-[var(--ink-soft)] leading-[1.55]">
                  {v.blurb}
                </p>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== EDUCATION + POSITIONS ===== */}
      <section data-testid="profile-education-positions" className="py-32 lg:py-44">
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 grid lg:grid-cols-12 gap-14 lg:gap-20">
          <aside className="lg:col-span-4">
            <ChapterMark numeral="V" label="Education" align="left" />
            <h2 className="mt-8 font-couture text-4xl lg:text-5xl tracking-[-0.01em] text-[var(--ink)] leading-[1.05]">
              The roots of
              <br />
              <span className="font-couture-italic text-[var(--gold-deep)]">
                a lifelong learner.
              </span>
            </h2>
            <div className="mt-10 p-8 border border-[var(--gold-line)] bg-[var(--bg-soft)]">
              <div className="flex items-start gap-5">
                <GraduationCap size={26} className="text-[var(--gold-deep)] mt-1 shrink-0" />
                <div>
                  <h3 className="font-couture text-2xl text-[var(--ink)] leading-snug">
                    {education.institution}
                  </h3>
                  <p className="mt-3 font-marcellus uppercase tracking-[0.36em] text-[0.66rem] text-[var(--gold-deep)]">
                    {education.degree}
                  </p>
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <ChapterMark numeral="VI" label="Positions & Responsibilities" align="left" />
            <h2 className="mt-8 mb-12 font-couture text-4xl lg:text-5xl tracking-[-0.01em] text-[var(--ink)] leading-[1.05]">
              Service beyond
              <span className="font-couture-italic text-[var(--gold-deep)]">
                {" "}the boardroom.
              </span>
            </h2>
            <motion.ul
              className="border-y border-[var(--gold-line)]"
              variants={staggerContainer(0.05)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
            >
              {positions.map((p, i) => (
                <motion.li
                  key={i}
                  data-testid={`position-${i}`}
                  variants={staggerItem}
                  className="flex items-baseline gap-6 lg:gap-10 py-6 hover:bg-[var(--bg-soft)] px-2 transition-colors border-b border-[var(--gold-line-soft)] last:border-b-0"
                >
                  <span className="numeral text-xl w-10 shrink-0 text-[var(--gold-deep)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-couture-italic text-xl lg:text-[1.4rem] text-[var(--ink)] leading-snug">
                    {p}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* ===== MEMBERSHIPS ===== */}
      <section
        data-testid="profile-memberships"
        className="py-32 lg:py-44 bg-[var(--bg-soft)]"
      >
        <div className="max-w-[1100px] mx-auto px-7 sm:px-12">
          <div className="text-center mb-16">
            <ChapterMark numeral="VII" label="Affiliations" />
            <h2 className="mt-10 font-couture text-5xl sm:text-6xl tracking-[-0.02em] leading-[1]">
              Networks
              <span className="font-couture-italic text-[var(--gold-deep)]">
                {" "}& associations.
              </span>
            </h2>
          </div>
          <motion.div
            className="grid sm:grid-cols-2 gap-x-14 gap-y-2"
            variants={staggerContainer(0.04)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            {memberships.map((m, i) => (
              <motion.div
                key={i}
                data-testid={`membership-${i}`}
                variants={staggerItem}
                className="flex items-baseline gap-5 py-4 border-b border-[var(--gold-line)]"
              >
                <span className="numeral text-base w-10 shrink-0 text-[var(--gold-deep)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-couture-italic text-lg lg:text-xl text-[var(--ink)]">
                  {m}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
